import React from 'react';
import { useDispatch } from 'react-redux';

import './IraWithholdingWarning.scss';

import Button from '../../Shared/Button/Button';
import If from '../../Shared/If/If';
import config from '../../../config/config';
import {
  closeModal,
  IRA_WITHHOLDING_WARNING_MODAL,
} from '../../../redux/modules/modals/modals.module';
import { setProgressFlag } from '../../../redux/modules/progress';
import { updateSellFund } from '../../../redux/modules/sellTransaction/sellTransaction.module';
import { PROGRESS_FLAG_SELL_WITHHOLDING_WARNED } from '../../../settings/settings';
import { dispatchPageLoad } from '../../../redux/utils/analytics-helper';
import { isSell, isRothConversion } from '../../../utils/transactions-util';
import { fullStateName } from '../../../utils/utils';

const IraWithholdingWarning = ({
  isSalesforceRepMessage,
  userState,
  showPageIraTable = false,
  mandatoryWithholdingStates,
  isRoth,
  agreementID,
  onConfirm = () => {},
}) => {
  const dispatch = useDispatch();
  const userStateIsMandatory = isRoth
    ? mandatoryWithholdingStates.indexOf(userState) > -1
    : false;

  //diff rules apply for DC, MI, and CT than for other mandatory states
  // MN is added for now with changes requested in US367474 MN will most likely
  // move out in the future
  const userStateSpecialThree = isRoth
    ? ['DC', 'MI', 'CT', 'MN', 'IA'].indexOf(userState) > -1
    : false;
  const stateToFull =
    userState === 'DC'
      ? 'Washington, D.C'
      : userState === 'MI'
      ? 'Michigan'
      : userState === 'CT'
      ? 'Connecticut'
      : userState === 'MN'
      ? 'Minnesota'
      : fullStateName(userState);

  const ROTH_WITHHOLDING_TEXT_NOTE = (
    <span>
      In addition, if you are requesting a qualified distribution, your address
      of record is in {stateToFull}, and you do not wish to withhold for state
      withholding, you must{' '}
      <a
        href="https://www.troweprice.com/personal-investing/help/contact/index.html"
        target="_blank"
        rel="noreferrer"
      >
        contact us
      </a>{' '}
      to request the distribution to prevent state income tax withholding.
    </span>
  );
  const ROTH_WITHHOLDING_TEXT = !userStateIsMandatory
    ? ' '
    : userStateSpecialThree
    ? ROTH_WITHHOLDING_TEXT_NOTE
    : ' Please note if you elect out of federal withholding, you will automatically be elected out of state withholding.';

  const closeIraWithholdingWarning = () => {
    dispatchPageLoad('Sell from IRA withholding warning accepted');
    dispatch(setProgressFlag(PROGRESS_FLAG_SELL_WITHHOLDING_WARNED));
    dispatch(closeModal(IRA_WITHHOLDING_WARNING_MODAL));
  };

  const setInputFocus = () => {
    if (isSell()) {
      dispatch(
        updateSellFund({
          agreementID,
          inFocus: true,
        })
      );
    }
  };
  return (
    <div className="e2e-ira-withholding-warning-container">
      <If test={!isSalesforceRepMessage}>
        <div className="ira-withholding-warning-heading">
          A Quick Note About Taxes
        </div>
      </If>
      <If test={isSalesforceRepMessage}>
        <span className="ira-withholding-warning-leader">
          Federal Income Tax Withholding Disclosure
        </span>
      </If>
      <ul className="ira-withholding-warning-list">
        <If test={!isSalesforceRepMessage}>
          <h1 className="ira-withholding-warning-leader">
            Federal Income Tax Withholding Disclosure
          </h1>
        </If>
        <If test={isSalesforceRepMessage}>
          <li className="ira-withholding-warning-item">
            <span className="ira-withholding-warning-leader">
              Launch the current{' '}
            </span>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://troweprice.egain.cloud/kb/retail/content/202200000002595/IRA-Redemption-Disclosures"
            >
              <u>federal withholding disclosures</u>
            </a>
            {'.'}
          </li>
        </If>
        <If test={showPageIraTable || !isSalesforceRepMessage}>
          <li className="ira-withholding-warning-item">
            <span className="ira-withholding-warning-leader">
              Your withholding rate is determined by the type of payment you
              will receive.
            </span>{' '}
            For nonperiodic payments, the default withholding is 10% and you can
            select to have a different rate by entering a rate between 0% and
            99% on the following step. You can't choose less than 10% for
            payments to be delivered outside the United States and its
            territories. Please{' '}
            <a href="https://www.troweprice.com/personal-investing/help/contact/index.html">
              contact us
            </a>{' '}
            if you want an election of 100%.
          </li>

          <li className="ira-withholding-warning-item">
            <span className="ira-withholding-warning-leader">Caution:</span> If
            you have too little tax withheld, you will generally owe tax when
            you file your tax return and may owe a penalty unless you make
            timely payments of estimated tax. If too much tax is withheld, you
            will generally be due a refund when you file your tax return. Your
            withholding choice (or an election not to have withholding on a
            nonperiodic payment) will generally apply to any future payment from
            the same IRA. Submit a new{' '}
            <a
              href="https://www.irs.gov/pub/irs-pdf/fw4r.pdf"
              rel="noreferrer"
              target="_blank"
              className="ira-withholding-warning-link"
            >
              Form W-4R{' '}
            </a>{' '}
            if you want to change your election.
          </li>

          <If test={isRoth}>
            <li className={'ira-withholding-warning-item'}>
              <span className="ira-withholding-warning-leader">
                IMPORTANT NOTE FOR ROTH IRAs:
              </span>{' '}
              If you are taking a Qualified Distribution from a Roth IRA and do
              not want federal income tax withheld, you will need to elect out
              of federal income tax withholding by choosing to decline or
              entering 0% in the withholding field.
              {!isRothConversion() &&
                userState !== 'AR' &&
                userState !== 'OK' &&
                ROTH_WITHHOLDING_TEXT}
              {!isRothConversion() &&
                (userState === 'AR' || userState === 'OK') && (
                  <>
                    {' '}
                    In addition, if you are requesting a qualified distribution,
                    your address of record is in {stateToFull}, and you do not
                    wish to withhold for state withholding, you must{' '}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${config.url}/personal-investing/help/contact/index.html`}
                    >
                      contact us
                    </a>{' '}
                    to request the distribution to prevent state income tax
                    withholding.
                  </>
                )}
            </li>
          </If>
        </If>
      </ul>
      <If test={!isSalesforceRepMessage}>
        <div className={'ira-withholding-ok-button'}>
          <Button
            id="ira-withholding-modal-close"
            buttonType="primary-large"
            className="ira-withholding-warning-ok-button btn-trp-primary-large e2e-ira-withholding-warning-ok-button"
            onClick={() => {
              setInputFocus();
              closeIraWithholdingWarning();
              onConfirm();
            }}
          >
            OK
          </Button>
        </div>
      </If>
    </div>
  );
};

export default IraWithholdingWarning;
